<script setup lang="ts">
/**
 * "This conversation is closed", and the way out of it — P2-52, ADR-0142.
 *
 * ONE COMPONENT FOR TWO SCREENS, because the two halves vary independently and
 * every combination is a real state:
 *
 *   chat screen, current conversation closed   both halves: the sentence and
 *                                              the button, with the composer
 *                                              hidden above them
 *   past screen, that conversation closed,     the sentence alone — the visitor
 *   current one still open                     has a live chat to go back to and
 *                                              offering to start another would
 *                                              be offering to abandon it
 *   past screen, current one closed or gone    both, and the button returns to
 *                                              the chat screen on its way
 *
 * `role="status"` rather than `alert`: this is the state of the screen the
 * visitor navigated to, not an interruption. It replaces the composer, and a
 * replaced composer with nothing in its place is a chat window that has silently
 * stopped accepting typing.
 */
import { useText } from '../vendor/i18n';
import { OwButton } from '../vendor/ui';

defineProps<{
  /** Whether the conversation being shown is itself closed. */
  readonly closed: boolean;
  /** Null when there is a live conversation to go back to instead. */
  readonly startNew: (() => void) | null;
}>();

const text = useText();
</script>

<template>
  <div v-if="closed || startNew !== null" class="ow-closed" role="status">
    <p v-if="closed" class="ow-closed__text">{{ text('widget.conversation.closed') }}</p>
    <OwButton v-if="startNew !== null" variant="primary" class="ow-closed__new" @click="startNew()">
      {{ text('widget.conversation.start_new') }}
    </OwButton>
  </div>
</template>
